import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Direction2D, Vector2D } from '@paragon/geometry-library';
import { Point2D } from '@paragon/three-shared';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { setEditInProgress } from '../../store/actions/editorActions';
import { EditingType } from '../../store/slices/editorSlice';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../util/events/types';

enum NudgeDirection {
  Left,
  Right,
  Up,
  Down,
  None,
}

const maxClickDuration = 250;

function getSnappedShiftAlongAxis(shift: Vector2D, axisDirection: Direction2D, snappingDistance: number) {
  const distanceAlongAxis = shift.getComponentInDirection(axisDirection);

  if (Math.abs(distanceAlongAxis) < snappingDistance) return Vector2D.zero;

  const roundedShiftDistance = Math.round(distanceAlongAxis / snappingDistance) * snappingDistance;
  return axisDirection.toVector(roundedShiftDistance);
}

function calculateSnappedShift(
  mousePosition: Point2D,
  lastMousePosition: Point2D,
  deltaLeftOverFromLastMove: Point2D,
  coordinateUpDirection: Point2D,
  snappingDistance: number,
) {
  const totalShift = new Vector2D(
    mousePosition.x - lastMousePosition.x + deltaLeftOverFromLastMove.x,
    mousePosition.y - lastMousePosition.y + deltaLeftOverFromLastMove.y,
  );
  const localUpAxis = new Direction2D(coordinateUpDirection.x, coordinateUpDirection.y);
  const localRightAxis = localUpAxis.rotateReverseQuarterTurn();

  const snappedShiftAlongUpAxis = getSnappedShiftAlongAxis(totalShift, localUpAxis, snappingDistance);
  const snappedShiftAlongRightAxis = getSnappedShiftAlongAxis(totalShift, localRightAxis, snappingDistance);

  const snappedShift = snappedShiftAlongUpAxis.add(snappedShiftAlongRightAxis);
  const leftOverShift = totalShift.subtract(snappedShift);

  return { snappedShift, leftOverShift };
}

interface NudgeSystemData {
  readonly editType: EditingType;
  readonly moveCallback: (x: number, y: number) => void;
  readonly postEditCallback: (() => void) | undefined;
  readonly coordinateUpDirection: Point2D;
  readonly snappingCoefficient: number | undefined;
}

export function useNudgeSystem({
  editType,
  moveCallback,
  postEditCallback,
  coordinateUpDirection,
  snappingCoefficient = 1,
}: NudgeSystemData) {
  const dispatch = useAppDispatch();

  const unscaledSnappingDistance = useAppSelector((state) => state.editor.snappingDistance);
  const snappingDistance = unscaledSnappingDistance * snappingCoefficient;

  const lastMousePositionRef = useRef<Point2D | null>(null);
  // Preserves the part of the delta that was discarded due to rounding so that we can apply it during the next move
  // event to avoid losing part of the mouse movement over several move events.
  const deltaLeftOverFromLastMoveRef = useRef<Point2D>({ x: 0, y: 0 });
  const directionRef = useRef<NudgeDirection>(NudgeDirection.None);
  const mouseUpTimeRef = useRef<number>(0);

  const endNudgeCallBack = useCallback(() => {
    dispatch(setEditInProgress(false));
    if (postEditCallback) {
      postEditCallback();
    }
  }, [dispatch, postEditCallback]);

  // any mouse up cancel all snapping movements
  const handleMouseUp = useCallback(() => {
    if (directionRef.current === NudgeDirection.None) return;
    if (new Date().getTime() < mouseUpTimeRef.current) {
      // Treat it like a click
      const nudgeVector = (() => {
        switch (directionRef.current) {
          case NudgeDirection.Left:
            return new Vector2D(-coordinateUpDirection.y, coordinateUpDirection.x);
          case NudgeDirection.Right:
            return new Vector2D(coordinateUpDirection.y, -coordinateUpDirection.x);
          case NudgeDirection.Up:
            return new Vector2D(coordinateUpDirection.x, coordinateUpDirection.y);
          case NudgeDirection.Down:
            return new Vector2D(-coordinateUpDirection.x, -coordinateUpDirection.y);
        }
      })();
      const { x, y } = nudgeVector.multiplyScalar(snappingDistance);
      moveCallback(x, y);
    }
    endNudgeCallBack();
    directionRef.current = NudgeDirection.None;
  }, [coordinateUpDirection.x, coordinateUpDirection.y, endNudgeCallBack, moveCallback, snappingDistance]);

  const handleMouseLeave = useCallback(() => {
    if (directionRef.current !== NudgeDirection.None) endNudgeCallBack();
    directionRef.current = NudgeDirection.None;
  }, [endNudgeCallBack]);

  const handleMouseMove = useCallback(
    (event: GlobalThreeJSDomObjectEvent) => {
      if (directionRef.current === NudgeDirection.None) return;
      const mousePosition = event.eventPosition.worldPosition;
      const lastMousePosition = lastMousePositionRef.current;
      if (!lastMousePosition) {
        lastMousePositionRef.current = mousePosition;
        return;
      }

      const { snappedShift, leftOverShift } = calculateSnappedShift(
        mousePosition,
        lastMousePosition,
        deltaLeftOverFromLastMoveRef.current,
        coordinateUpDirection,
        snappingDistance,
      );
      lastMousePositionRef.current = mousePosition;
      deltaLeftOverFromLastMoveRef.current = leftOverShift;

      if (snappedShift.x !== 0 || snappedShift.y !== 0) moveCallback(snappedShift.x, snappedShift.y);
    },
    [coordinateUpDirection, moveCallback, snappingDistance],
  );

  useEffect(() => {
    threeJsDom.addGlobalListener('mousemove', handleMouseMove, 1);
    threeJsDom.addGlobalListener('mouseup', handleMouseUp, -1);
    threeJsDom.addGlobalListener('mouseleave', handleMouseLeave);

    return () => {
      threeJsDom.removeGlobalListener('mousemove', handleMouseMove);
      threeJsDom.removeGlobalListener('mouseup', handleMouseUp);
      threeJsDom.removeGlobalListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseLeave, handleMouseMove, handleMouseUp]);

  const onStartNudge = useCallback(
    (direction: NudgeDirection) => () => {
      dispatch(setEditInProgress(editType));
      deltaLeftOverFromLastMoveRef.current = { x: 0, y: 0 };
      lastMousePositionRef.current = null;
      mouseUpTimeRef.current = new Date().getTime() + maxClickDuration;
      directionRef.current = direction;
    },
    [dispatch, editType],
  );

  const startLeft = useMemo(() => onStartNudge(NudgeDirection.Left), [onStartNudge]);
  const startRight = useMemo(() => onStartNudge(NudgeDirection.Right), [onStartNudge]);
  const startUp = useMemo(() => onStartNudge(NudgeDirection.Up), [onStartNudge]);
  const startDown = useMemo(() => onStartNudge(NudgeDirection.Down), [onStartNudge]);

  return { startLeft, startRight, startUp, startDown };
}
